/**
 * The sheet element's view-model swap bracket and the editor-observer hold
 * it uses, over the element's `SheetElementPort`. Moved out of
 * sheet-element.ts (r5 FS 2). This module owns `ViewModelSwapState`, the
 * bracket's depth and suppressed-pass count.
 */
import { ProseMirrorEditor } from "@tento-ui/components/prosemirror";
import { type Option } from "@tento-lona/sheets";
import { Tracing } from "@tento-core/tracing";
import { $$ } from "@tento-ui/fastdom";
import { ObserverSettle } from "./observer-settle.ts";
import type { SheetElementPort } from "./sheet-element-port.ts";

/** Hold the editors' DOM observers until the current bind settles (or `signal` aborts). */
export function holdEditorObservers(port: SheetElementPort, signal: Option<AbortSignal>): () => void {
  const scroll = port.$$.root.scrollPort;
  const hold = new ObserverSettle({
    suspend: () => ProseMirrorEditor.suspendObservers(),
    resume: () => ProseMirrorEditor.resumeObservers(),
    wait: (signal) => scroll.whenSettled({
      kind: "this-bind",
      generation: scroll.bindQueue().current,
    }, signal),
    pending: () => scroll.bindQueue().kind === "pending",
    afterMutate: (callback) => $$.mutateEnd(callback),
    setTimeout: (callback, milliseconds) => window.setTimeout(callback, milliseconds),
    clearTimeout: (id) => window.clearTimeout(id),
    finished: () => {
      port.untrackSwapSettle(cancel);
      signal?.removeEventListener("abort", cancel);
    },
  });
  const cancel = () => hold.cancel();
  port.trackSwapSettle(cancel);
  if (signal?.aborted) cancel();
  else signal?.addEventListener("abort", cancel, { once: true });
  return () => hold.release();
}

/**
 * While a view-model replacement is in flight (the app is awaiting
 * `clone.withViewConfig` and will bind the successor momentarily), the
 * whole-sheet invalidation passes stand down: the clone's handle mutations
 * (system-row swap, forced alias re-expansion) land as structural
 * invalidations against the STILL-MOUNTED predecessor, and the full
 * `bindSheet` they trigger — blank window included — is superseded by the
 * successor's own full rebind one tick later (the render-hotpath
 * "two blank passes per switch" finding). Narrow effects keep flowing so
 * the old view stays current while it remains on screen. The returned
 * release runs one coalesced structural pass ONLY when passes were
 * suppressed and no full rebind arrived (the abandoned-swap path — e.g.
 * the clone threw), so no invalidation is ever lost.
 */
export function beginViewModelSwap(port: SheetElementPort): () => void {
  port.bindReasons.contentChanged();
  const swap = port.swapState;
  swap.viewModelSwapDepth++;
  // Bystander guard: the swap's wholesale cell rebind fires every live
  // ProseMirror view's DOMObserver into deep selection/node compares
  // (measured at 39% of the switch flush). Their own DOM is not touched --
  // content replacements destroy and recreate views -- so the observers
  // stand down for the bracket.
  const releaseObservers = holdEditorObservers(port, null);
  const genAtBegin = port.bindState.bindGeneration;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    // Defer the actual release one fastdom cycle: the clone's structural
    // storm schedules its invalidation flush via `$$.mutate`, which fires
    // in the NEXT frame's write phase — after the caller's `finally` runs.
    // Releasing at that frame's `mutateEnd` keeps the hold open across the
    // flush, so the whole-sheet pass it would run against the just-bound
    // successor is suppressed too. The successor's own drain re-reads live
    // engine state per column (the N3 idempotent/data-current contract),
    // so the suppressed pass is redundant even for events that raced in
    // behind the clone's storm.
    $$.mutateEnd(() => {
      releaseObservers();
      swap.viewModelSwapDepth--;
      if (swap.viewModelSwapDepth > 0) return;
      const suppressed = swap.swapSuppressedPasses;
      swap.swapSuppressedPasses = 0;
      if (suppressed > 0 && port.bindState.bindGeneration === genAtBegin) {
        Tracing.log("sheet/swap-suppressed-recovery", { suppressed });
        void port.rebindHandle.rebindStructure();
      }
    });
  };
}

/** True (and counted) when a whole-sheet pass should stand down for an
 *  in-flight view-model swap — see {@link beginViewModelSwap}. */
export function suppressForViewModelSwap(port: SheetElementPort, pass: string): boolean {
  if (port.swapState.viewModelSwapDepth === 0) return false;
  port.swapState.swapSuppressedPasses++;
  Tracing.log("sheet/swap-suppress", { pass });
  return true;
}
